import rolePermissions from '../config/rolePermissions.js';

export const ROLE_DEFINITIONS = rolePermissions?.roles || {};

// These capabilities are the server-enforced permissions already carried in
// the signed session. Page selections below expand into this complete map so
// a hidden page never becomes a client-side-only restriction.
export const MODULE_DEFINITIONS = [
    { key: 'dashboard', label: 'لوحة التحكم', actions: ['view'] },
    { key: 'operations', label: 'صفحة العمليات', actions: ['view', 'settle', 'edit', 'delete'] },
    { key: 'journal_entries', label: 'دفتر اليومية', actions: ['view', 'create', 'edit', 'delete', 'pos'] },
    { key: 'archive', label: 'الأرشيف', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'vehicles', label: 'ملفات المركبات', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'customers', label: 'العملاء', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'invoices', label: 'الفواتير', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'debts', label: 'الذمم', actions: ['view', 'settle'] },
    { key: 'inventory', label: 'المخزون', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'work_orders', label: 'أوامر الشغل', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'reports', label: 'التقارير', actions: ['view', 'revenue'] },
    { key: 'users', label: 'المستخدمين', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'settings', label: 'الإعدادات', actions: ['view', 'edit'] },
    { key: 'approvals', label: 'الاعتمادات والتشغيل الحساس', actions: ['view', 'approve'] },
];

export const ACTION_LABELS = {
    view: 'عرض',
    create: 'إضافة',
    edit: 'تعديل وحفظ',
    delete: 'حذف',
    settle: 'تسوية / سداد',
    pos: 'نقطة البيع',
    revenue: 'تقارير الإيرادات',
    vehicle_create: 'إضافة مركبة',
    vehicle_edit: 'تعديل وحفظ مركبة',
    vehicle_finalize: 'اعتماد نهائي للزيارة',
    operation_settle: 'تحصيل / سداد',
    approve: 'اعتماد وتنفيذ حساس',
};

const grant = (module, action) => ({ module, action });

// A page is an explicit product surface. Its actions expand to the existing
// backend capabilities, which are validated by authz on every API request.
// This lets an administrator grant Dashboard + add vehicle without exposing
// the Vehicles screen in the sidebar.
export const PAGE_DEFINITIONS = [
    {
        key: 'dashboard', label: 'لوحة التحكم', route: '/', actions: [
            { key: 'view', grants: [grant('dashboard', 'view')] },
            { key: 'vehicle_create', grants: [grant('vehicles', 'create')] },
            { key: 'vehicle_edit', grants: [grant('vehicles', 'view'), grant('vehicles', 'edit'), grant('work_orders', 'edit')] },
            { key: 'vehicle_finalize', grants: [grant('work_orders', 'edit')] },
            { key: 'operation_settle', grants: [grant('operations', 'settle')] },
        ],
    },
    { key: 'operations', label: 'صفحة العمليات', route: '/operations', actions: ['view', 'settle', 'edit', 'delete'].map(key => ({ key, grants: [grant('operations', key)] })) },
    { key: 'debts', label: 'متابعة الذمم والتحصيل', route: '/debts-followup', actions: [
        { key: 'view', grants: [grant('debts', 'view')] },
        // The canonical collection endpoint retains its legacy operations
        // capability check; the signed page action keeps it Debt-page scoped.
        { key: 'settle', grants: [grant('debts', 'settle'), grant('operations', 'settle')] },
    ] },
    { key: 'archive', label: 'الأرشيف', route: '/archive', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('archive', key)] })) },
    { key: 'customers', label: 'العملاء', route: '/customers', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('customers', key)] })) },
    { key: 'technicians', label: 'الفنيون', route: '/technicians', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('work_orders', key)] })) },
    { key: 'parts_dashboard', label: 'لوحة تحكم القطع', route: '/parts-dashboard', actions: [{ key: 'view', grants: [grant('inventory', 'view')] }] },
    { key: 'parts', label: 'المخزون والقطع', route: '/parts', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('inventory', key)] })) },
    // Backend authz protects /api/services through inventory, not work_orders.
    { key: 'services', label: 'الخدمات', route: '/services', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('inventory', key)] })) },
    { key: 'suppliers', label: 'الموردون', route: '/suppliers', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('inventory', key)] })) },
    { key: 'invoices', label: 'الفواتير', route: '/finance/invoices', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('invoices', key)] })) },
    { key: 'chart_of_accounts', label: 'دليل الحسابات', route: '/accounting/chart-of-accounts', actions: [{ key: 'view', grants: [grant('reports', 'view')] }] },
    { key: 'financial_statements', label: 'القوائم المالية', route: '/accounting/comprehensive', actions: [{ key: 'view', grants: [grant('reports', 'view')] }] },
    { key: 'journal_entries', label: 'دفتر اليومية', route: '/accounting/journal-entries', actions: [
        ...['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('journal_entries', key)] })),
        // The existing journal screen includes a settlement button.  Its
        // legacy server contract requires operations.settle, while the page
        // action prevents that shared capability from authorizing POS.
        { key: 'settle', grants: [grant('journal_entries', 'view'), grant('operations', 'settle')] },
    ] },
    { key: 'pos', label: 'نقطة البيع الذكية', route: '/accounting/journal-entries?view=pos', actions: [
        { key: 'view', grants: [grant('journal_entries', 'view'), grant('journal_entries', 'pos')] },
        { key: 'create', grants: [grant('journal_entries', 'view'), grant('journal_entries', 'pos'), grant('journal_entries', 'create')] },
        // POS settlement remains a distinct manager-selected action.  The
        // existing server route requires operations.settle, while the signed
        // page map lets authz distinguish it from the Operations page.
        { key: 'settle', grants: [grant('journal_entries', 'view'), grant('journal_entries', 'pos'), grant('operations', 'settle')] },
    ] },
    { key: 'firewall', label: 'جدار حماية المحاسبة', route: '/accounting/firewall', actions: [{ key: 'view', grants: [grant('reports', 'view')] }] },
    { key: 'taxes', label: 'الضرائب', route: '/finance/taxes', actions: [{ key: 'view', grants: [grant('reports', 'view')] }] },
    { key: 'system_audit', label: 'سجل النظام', route: '/system-audit', actions: [{ key: 'view', grants: [grant('reports', 'view')] }] },
    { key: 'print', label: 'طباعة المستندات', route: '/print', actions: [{ key: 'view', grants: [grant('invoices', 'view')] }] },
    // The server contracts document-template reads/writes to settings. Keeping
    // that grant explicit avoids a false UI promise of template-only access.
    { key: 'templates', label: 'قوالب المستندات', route: '/templates', actions: [
        { key: 'view', grants: [grant('settings', 'view')] },
        { key: 'edit', grants: [grant('settings', 'view'), grant('settings', 'edit')] },
    ] },
    { key: 'settings', label: 'إعدادات النظام', route: '/settings', actions: ['view', 'edit'].map(key => ({ key, grants: [grant('settings', key)] })) },
    { key: 'users', label: 'المستخدمون والصلاحيات', route: '/settings?tab=users', actions: ['view', 'create', 'edit', 'delete'].map(key => ({ key, grants: [grant('users', key)] })) },
    { key: 'approvals', label: 'الاعتمادات والتشغيل الحساس', route: null, actions: ['view', 'approve'].map(key => ({ key, grants: [grant('approvals', key)] })) },
    { key: 'import', label: 'استيراد البيانات', route: '/settings?tab=import', actions: [{ key: 'create', grants: [grant('inventory', 'create')] }] },
];

const LEGACY_PERMISSION_MAP = {
    canViewDashboard: { module: 'dashboard', actions: ['view'] },
    canManageVehicles: { module: 'vehicles', actions: ['view', 'create', 'edit', 'delete'] },
    canManageCustomers: { module: 'customers', actions: ['view', 'create', 'edit', 'delete'] },
    canManageParts: { module: 'inventory', actions: ['view', 'create', 'edit', 'delete'] },
    canManageServices: { module: 'work_orders', actions: ['view', 'create', 'edit', 'delete'] },
    canViewReports: { module: 'reports', actions: ['view'] },
    canManageFinance: { module: 'debts', actions: ['view', 'settle'] },
    canManageUsers: { module: 'users', actions: ['view', 'create', 'edit', 'delete'] },
    canManageSettings: { module: 'settings', actions: ['view', 'edit'] },
};

export const clonePermissions = (permissions) => JSON.parse(JSON.stringify(permissions || {}));
export const getRolePermissions = (role) => ROLE_DEFINITIONS?.[role]?.permissions || {};
export const getRoleLabel = (role) => ROLE_DEFINITIONS?.[role]?.name || role || '';
export const isLegacyPermissions = (permissions) => Boolean(permissions && typeof permissions === 'object' && Object.keys(permissions).some(key => key.startsWith('can')));

const mergePermissions = (base, overrides) => {
    const merged = clonePermissions(base);
    Object.entries(overrides || {}).forEach(([module, actions]) => {
        if (!actions || typeof actions !== 'object')
            return;
        if (module === 'pages') {
            merged.pages = clonePermissions(actions);
            return;
        }
        merged[module] = { ...(merged[module] || {}) };
        Object.entries(actions).forEach(([action, allowed]) => {
            if (typeof allowed === 'boolean')
                merged[module][action] = allowed;
        });
    });
    return merged;
};

export const normalizePermissions = (permissions, role) => {
    const base = clonePermissions(getRolePermissions(role));
    if (!permissions || typeof permissions !== 'object' || Object.keys(permissions).length === 0)
        return base;
    if (!isLegacyPermissions(permissions)) {
        const next = clonePermissions(permissions);
        if (!next.operations && next.work_orders) {
            next.operations = { view: next.work_orders.view === true, settle: next.work_orders.edit === true || next.debts?.settle === true, edit: next.work_orders.edit === true, delete: next.work_orders.delete === true };
        }
        if (!next.journal_entries && next.reports) {
            next.journal_entries = { view: next.reports.view === true, create: next.reports.create === true, edit: next.reports.edit === true, delete: next.reports.delete === true, pos: next.reports.view === true };
        }
        if (!next.archive && next.vehicles) {
            next.archive = { view: next.vehicles.view === true, create: next.vehicles.create === true, edit: next.vehicles.edit === true, delete: next.vehicles.delete === true };
        }
        return mergePermissions(base, next);
    }
    const next = {};
    Object.entries(LEGACY_PERMISSION_MAP).forEach(([legacyKey, config]) => {
        if (!permissions[legacyKey])
            return;
        config.actions.forEach((action) => {
            next[config.module] = { ...(next[config.module] || {}), [action]: true };
        });
    });
    return mergePermissions(base, next);
};

export const hasPermission = (session, moduleKey, action = 'view') => {
    if (!moduleKey)
        return true;
    const permissions = normalizePermissions(session?.permissions, session?.role);
    return permissions?.[moduleKey]?.[action] === true;
};
export const hasAnyPermission = (session, rules = []) => (rules || []).some(rule => hasPermission(session, rule.module, rule.action || 'view'));

export const hasPageScope = (session) => Boolean(session?.permissions && Object.prototype.hasOwnProperty.call(session.permissions, 'pages') && session.permissions.pages && typeof session.permissions.pages === 'object');

export const getPagePermissions = (permissions, role) => {
    if (permissions?.pages && typeof permissions.pages === 'object')
        return clonePermissions(permissions.pages);
    return derivePagePermissions(normalizePermissions(permissions, role));
};

export const hasPagePermission = (session, pageKey, action = 'view') => {
    if (!hasPageScope(session))
        return true;
    return session.permissions?.pages?.[pageKey]?.[action] === true;
};

const hasAllGrants = (permissions, grants = []) => grants.every(({ module, action }) => permissions?.[module]?.[action] === true);

// Existing users without a pages map retain their current behavior until an
// administrator saves them. That makes the migration explicit and reversible.
export const derivePagePermissions = (permissions) => PAGE_DEFINITIONS.reduce((result, page) => {
    result[page.key] = page.actions.reduce((actions, action) => ({
        ...actions,
        [action.key]: hasAllGrants(permissions, action.grants),
    }), {});
    return result;
}, {});

const blankCapabilityMap = () => MODULE_DEFINITIONS.reduce((result, module) => ({
    ...result,
    [module.key]: module.actions.reduce((actions, action) => ({ ...actions, [action]: false }), {}),
}), {});

// Persist a complete explicit capability map. The full map intentionally
// replaces role defaults for an edited user; otherwise an unselected action
// could be inherited from a broad role when the API validates the request.
export const buildPageScopedPermissions = (pages) => {
    const normalizedPages = clonePermissions(pages);
    const capabilities = blankCapabilityMap();
    PAGE_DEFINITIONS.forEach((page) => {
        page.actions.forEach((action) => {
            if (normalizedPages?.[page.key]?.[action.key] !== true)
                return;
            action.grants.forEach(({ module, action: capability }) => {
                if (capabilities[module] && Object.prototype.hasOwnProperty.call(capabilities[module], capability))
                    capabilities[module][capability] = true;
            });
        });
    });
    return { ...capabilities, pages: normalizedPages };
};

const pageRule = (page, pageAction = 'view', extra = {}) => ({ page, pageAction, ...extra });

export const ROUTE_PERMISSIONS = [
    { pattern: /^\/account\/security/, allow: 'authenticated' },
    { pattern: /^\/$/, ...pageRule('dashboard'), module: 'dashboard', action: 'view' },
    { pattern: /^\/operations/, ...pageRule('operations'), module: 'operations', action: 'view' },
    { pattern: /^\/new-vehicle/, ...pageRule('dashboard', 'vehicle_create'), anyOf: [grant('vehicles', 'create'), grant('archive', 'create')] },
    { pattern: /^\/vehicle\//, pageAnyOf: [pageRule('dashboard', 'vehicle_edit'), pageRule('archive')], anyOf: [grant('vehicles', 'view'), grant('archive', 'view')] },
    { pattern: /^\/customers/, ...pageRule('customers'), module: 'customers', action: 'view' },
    { pattern: /^\/customer\//, ...pageRule('customers'), module: 'customers', action: 'view' },
    { pattern: /^\/technicians/, ...pageRule('technicians'), module: 'work_orders', action: 'view' },
    { pattern: /^\/suppliers/, ...pageRule('suppliers'), module: 'inventory', action: 'view' },
    { pattern: /^\/parts-dashboard/, ...pageRule('parts_dashboard'), module: 'inventory', action: 'view' },
    { pattern: /^\/parts/, ...pageRule('parts'), module: 'inventory', action: 'view' },
    { pattern: /^\/services/, ...pageRule('services'), module: 'inventory', action: 'view' },
    { pattern: /^\/invoice-templates/, ...pageRule('templates'), module: 'settings', action: 'view' },
    { pattern: /^\/finance\/invoices/, ...pageRule('invoices'), module: 'invoices', action: 'view' },
    { pattern: /^\/finance\/taxes/, ...pageRule('taxes'), module: 'reports', action: 'view' },
    { pattern: /^\/accounting\/journal-entries/, ...pageRule('journal_entries'), module: 'journal_entries', action: 'view' },
    { pattern: /^\/accounting\/chart-of-accounts/, ...pageRule('chart_of_accounts'), module: 'reports', action: 'view' },
    { pattern: /^\/accounting\/comprehensive/, ...pageRule('financial_statements'), module: 'reports', action: 'view' },
    { pattern: /^\/accounting\/firewall/, ...pageRule('firewall'), module: 'reports', action: 'view' },
    { pattern: /^\/accounting\//, module: 'reports', action: 'view' },
    { pattern: /^\/ai-financial/, ...pageRule('firewall'), module: 'reports', action: 'view' },
    { pattern: /^\/system-audit/, ...pageRule('system_audit'), module: 'reports', action: 'view' },
    { pattern: /^\/archive/, ...pageRule('archive'), module: 'archive', action: 'view' },
    { pattern: /^\/import/, ...pageRule('import', 'create'), module: 'inventory', action: 'create' },
    { pattern: /^\/users/, ...pageRule('users'), module: 'users', action: 'view' },
    { pattern: /^\/settings/, ...pageRule('settings'), module: 'settings', action: 'view' },
    { pattern: /^\/profile/, ...pageRule('settings'), module: 'settings', action: 'view' },
    { pattern: /^\/quotations/, ...pageRule('print'), module: 'invoices', action: 'view' },
    { pattern: /^\/print/, ...pageRule('print'), module: 'invoices', action: 'view' },
    { pattern: /^\/templates/, ...pageRule('templates'), module: 'settings', action: 'view' },
    { pattern: /^\/debts-followup/, ...pageRule('debts'), module: 'debts', action: 'view' },
];

const SMART_POS_ROUTE_PERMISSION = { ...pageRule('pos'), allOf: [grant('journal_entries', 'view'), grant('journal_entries', 'pos')] };

export const resolveRoutePermission = (path) => {
    if (!path)
        return null;
    const [rawPathname, query = ''] = String(path).split('?', 2);
    const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname;
    if (pathname === '/settings') {
        const tab = new URLSearchParams(query).get('tab');
        if (tab === 'users')
            return { ...pageRule('users'), module: 'users', action: 'view' };
        if (tab === 'import')
            return { ...pageRule('import', 'create'), module: 'inventory', action: 'create' };
        if (tab === 'financial-reset')
            return { ...pageRule('settings', 'edit'), roles: ['admin'] };
    }
    if (pathname === '/accounting/journal-entries' && new URLSearchParams(query).get('view') === 'pos')
        return SMART_POS_ROUTE_PERMISSION;
    return ROUTE_PERMISSIONS.find(rule => rule.pattern.test(pathname)) || null;
};

const pageRuleAllows = (session, routeRule) => {
    if (Array.isArray(routeRule.pageAnyOf))
        return routeRule.pageAnyOf.some(rule => hasPagePermission(session, rule.page, rule.pageAction || 'view'));
    if (routeRule.page)
        return hasPagePermission(session, routeRule.page, routeRule.pageAction || 'view');
    return true;
};

export const hasRoutePermission = (session, routeRule) => {
    if (!routeRule)
        return true;
    if (routeRule.allow === 'authenticated') {
        const userId = String(session?.id || session?.user_id || '').trim();
        const username = String(session?.username || '').trim();
        return Boolean(userId && username);
    }
    // Once an administrator has explicitly scoped pages, an unclassified
    // route must never inherit a broad module rule (for example /accounting/*).
    if (hasPageScope(session) && (!routeRule.page && !Array.isArray(routeRule.pageAnyOf) || !pageRuleAllows(session, routeRule)))
        return false;
    if (Array.isArray(routeRule.roles) && !routeRule.roles.includes(String(session?.role || '').toLowerCase()))
        return false;
    if (Array.isArray(routeRule.allOf))
        return routeRule.allOf.every(rule => hasPermission(session, rule.module, rule.action || 'view'));
    if (Array.isArray(routeRule.anyOf))
        return hasAnyPermission(session, routeRule.anyOf);
    if (routeRule.module)
        return hasPermission(session, routeRule.module, routeRule.action || 'view');
    return true;
};

export const FIRST_ALLOWED_ROUTES = PAGE_DEFINITIONS.filter(page => page.route).map(page => page.route);
export const getFirstAllowedRoute = (session) => FIRST_ALLOWED_ROUTES.find(path => hasRoutePermission(session, resolveRoutePermission(path))) || '/login';
